/**
 * Lo pendiente del back office, en palabras nuestras · 13-10-2026
 *
 * `GET /admin/pending` cuenta la rutina de cada mañana (manual del back office, §2):
 * a quién llamar, qué reclamación espera, qué proceso falló anoche. Aquí cada código
 * tiene su frase, su icono y **la pestaña donde se resuelve**, igual que en el panel
 * (`club/pendientes.ts`). El servidor manda hasta tres nombres de club por código, y
 * van en la frase: «tres clubes sin contrato» obliga a abrir la lista; «Dojang
 * Pascual, Club Norte y 1 más» ya dice si es el que llamó ayer.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { cuenta } from '@/shared/ui/formato';
import type { Pendiente } from '@/shared/ui/pendientes/tipos';

export interface PendienteDeKiap {
  readonly code: string;
  readonly severity: 'high' | 'medium';
  readonly count: number;
  readonly examples: readonly string[];
}

export interface PendientesDeKiap {
  readonly pending: readonly PendienteDeKiap[];
  readonly generated_at: string;
}

/** «Dojang Pascual, Club Norte y 3 más.» Sin nombres, nada. */
function quienes(p: PendienteDeKiap): string {
  if (p.examples.length === 0) return '';
  const resto = p.count - p.examples.length;
  const nombres = p.examples.join(', ');
  return resto > 0 ? `${nombres} y ${resto} más.` : `${nombres}.`;
}

/** La frase del porqué, y detrás quiénes son. */
const con = (porque: string, p: PendienteDeKiap) =>
  [quienes(p), porque].filter(Boolean).join(' ');

export function aPendientesDeKiap(
  lista: readonly PendienteDeKiap[]
): readonly Pendiente[] {
  return lista.flatMap((p): Pendiente[] => {
    const n = p.count;
    const base = { clave: p.code, gravedad: p.severity };

    switch (p.code) {
      case 'plan_alerts':
        return [
          {
            ...base,
            icono: 'euro',
            titulo:
              n === 1
                ? 'Un club al que llamar por su plan'
                : `${n} clubes a los que llamar por su plan`,
            detalle: con(
              p.severity === 'high'
                ? 'Alguno ya tiene el panel en solo lectura.'
                : 'Pago en su gracia o por encima de sus límites.',
              p
            ),
            accion: 'Ver a quién llamar',
            destino: '/admin/dinero/avisos',
          },
        ];
      case 'claims_pending':
        return [
          {
            ...base,
            icono: 'directorio',
            titulo:
              n === 1
                ? 'Una ficha reclamada por decidir'
                : `${n} fichas reclamadas por decidir`,
            detalle: con(
              'Quien reclama un martes y sigue esperando el viernes ya no vuelve.',
              p
            ),
            accion: 'Decidir',
            destino: '/admin/directorio/reclamaciones',
          },
        ];
      case 'removals_pending':
        return [
          {
            ...base,
            icono: 'directorio',
            titulo:
              n === 1
                ? 'Una baja del directorio sin confirmar'
                : `${n} bajas del directorio sin confirmar`,
            detalle: con(
              'La ficha ya está fuera; falta decírselo a quien lo pidió.',
              p
            ),
            accion: 'Confirmar',
            destino: '/admin/directorio/bajas',
          },
        ];
      case 'jobs_failing':
        return [
          {
            ...base,
            icono: 'operacion',
            titulo:
              n === 1
                ? 'Un proceso programado con problemas'
                : `${n} procesos programados con problemas`,
            detalle: con(
              n === 1
                ? 'Ha fallado en las últimas 24 horas o lleva más de lo debido sin correr.'
                : 'Han fallado en las últimas 24 horas o llevan más de lo debido sin correr.',
              p
            ),
            accion: 'Ver los procesos',
            destino: '/admin/operacion/procesos',
          },
        ];
      case 'data_requests_due':
        return [
          {
            ...base,
            icono: 'escudo',
            titulo: `${cuenta(n, 'solicitud de datos vence', 'solicitudes de datos vencen')} en una semana`,
            detalle: con(
              'Responde el club; llámale antes de que se cumpla el mes legal.',
              p
            ),
            accion: 'Ver las solicitudes',
            destino: '/admin/operacion/datos',
          },
        ];
      case 'webhooks_unprocessed':
        return [
          {
            ...base,
            icono: 'pulso',
            titulo: `${cuenta(n, 'webhook', 'webhooks')} sin procesar`,
            detalle:
              'Cada uno es dinero que un club cree que ha entrado y no consta.',
            accion: 'Revisarlos',
            destino: '/admin/operacion/webhooks',
          },
        ];
      case 'leads_overdue':
        return [
          {
            ...base,
            icono: 'comercial',
            titulo:
              n === 1
                ? 'Un lead con la próxima acción pasada'
                : `${n} leads con la próxima acción pasada`,
            detalle: con('Un lead que espera se enfría.', p),
            accion: 'Ir a comercial',
            destino: '/admin/comercial',
          },
        ];
      case 'dpa_unsigned':
        return [
          {
            ...base,
            icono: 'contrato',
            titulo: `${cuenta(n, 'club', 'clubes')} sin el contrato de encargo`,
            detalle: con(
              'Lo firma su dueño en el panel, en Cumplimiento. Recuérdaselo.',
              p
            ),
            accion: 'Ver los clubes',
            destino: '/admin/clubes',
          },
        ];
      case 'owners_pending':
        return [
          {
            ...base,
            icono: 'invitar',
            titulo:
              n === 1
                ? 'Un dueño invitado que no ha entrado'
                : `${n} dueños invitados que no han entrado`,
            detalle: con(
              'Hace más de tres días. La invitación se reenvía desde la ficha del club.',
              p
            ),
            accion: 'Ver los clubes',
            destino: '/admin/clubes',
          },
        ];
      case 'clubs_without_plan':
        return [
          {
            ...base,
            icono: 'euro',
            titulo:
              n === 1
                ? 'Un club con dueño y sin plan'
                : `${n} clubes con dueño y sin plan`,
            detalle: con(
              'Sin plan no se corta nada: lo tienen todo gratis. Ponle uno en su ficha.',
              p
            ),
            accion: 'Ver los clubes',
            destino: '/admin/clubes',
          },
        ];
      case 'health_critical':
        return [
          {
            ...base,
            icono: 'soporte',
            titulo: `${cuenta(n, 'club', 'clubes')} con la salud en crítico`,
            detalle: con('Si nadie llama, se da de baja.', p),
            accion: 'Ver la salud',
            destino: '/admin/soporte/salud',
          },
        ];
      default:
        // Un código que este back office aún no conoce no se pinta.
        return [];
    }
  });
}

/**
 * La consulta. Cada dos minutos se da por vieja y cada diez se repite sola; al
 * cambiar de pantalla, si tiene más de dos minutos, también: confirmas una baja,
 * vuelves y la campana ya no la cuenta.
 */
export function usePendientesDeKiap() {
  const camino = useRouterState({ select: (s) => s.location.pathname });

  const consulta = useQuery({
    queryKey: ['admin', 'pendientes'],
    staleTime: 2 * 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.v1.admin.pending.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PendientesDeKiap;
    },
  });

  const { refetch, dataUpdatedAt } = consulta;
  useEffect(() => {
    if (dataUpdatedAt && Date.now() - dataUpdatedAt > 2 * 60_000) {
      void refetch();
    }
  }, [camino, refetch, dataUpdatedAt]);

  return consulta;
}
